import React, { useState, useCallback, useMemo } from 'react';
import {
  Paper,
  Typography,
  IconButton,
  Button,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  ViewWeek,
  ViewDay,
  CalendarMonth,
  Add as AddIcon,
  FilterList,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { Task } from '../../services/api/todo.service';

interface CalendarViewProps {
  tasks: Task[];
  onTaskClick?: (taskId: string) => void;
  onTaskCreate?: (taskData: any) => Promise<void>;
  onTaskUpdate?: (taskId: string, updates: any) => Promise<void>;
  onTaskDelete?: (taskId: string) => Promise<void>;
  onTaskReorder?: (tasks: { task_id: string; daily_rank: number; due_date: string }[]) => Promise<void>;
  loading?: boolean;
  viewType?: 'month' | 'week' | 'day';
  onViewTypeChange?: (viewType: 'month' | 'week' | 'day') => void;
  currentDate?: Date;
  onDateChange?: (date: Date) => void;
  showAssignedToOthers?: boolean;
  onShowAssignedToOthersChange?: (show: boolean) => void;
}

type ViewType = 'month' | 'week' | 'day';

// Status colors
const STATUS_COLORS: Record<string, string> = {
  pending: '#FFA726',
  in_progress: '#42A5F5',
  blocked: '#FF7043',
  completed: '#66BB6A',
  cancelled: '#EF5350',
  in_review: '#AB47BC'
};

// Priority colors
const PRIORITY_COLORS: Record<string, string> = {
  low: '#81C784',
  medium: '#FFB74D',
  high: '#FF8A65',
  urgent: '#E57373'
};

export const CalendarView: React.FC<CalendarViewProps> = ({
  tasks,
  onTaskClick,
  onTaskCreate,
  onTaskUpdate,
  onTaskDelete,
  onTaskReorder,
  loading = false,
  viewType = 'month',
  onViewTypeChange,
  currentDate = new Date(),
  onDateChange,
  showAssignedToOthers = false,
  onShowAssignedToOthersChange
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(currentDate);
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    task: Task;
  } | null>(null);

  // Quick task creation dialog state
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [quickCreateDate, setQuickCreateDate] = useState<string>('');
  const [quickTaskTitle, setQuickTaskTitle] = useState('');

  // Calendar navigation
  const navigateDate = useCallback((direction: 'prev' | 'next' | 'today') => {
    let newDate = new Date(selectedDate);

    switch (direction) {
      case 'prev':
        if (viewType === 'month') {
          newDate.setMonth(newDate.getMonth() - 1);
        } else if (viewType === 'week') {
          newDate.setDate(newDate.getDate() - 7);
        } else {
          newDate.setDate(newDate.getDate() - 1);
        }
        break;
      case 'next':
        if (viewType === 'month') {
          newDate.setMonth(newDate.getMonth() + 1);
        } else if (viewType === 'week') {
          newDate.setDate(newDate.getDate() + 7);
        } else {
          newDate.setDate(newDate.getDate() + 1);
        }
        break;
      case 'today':
        newDate = new Date();
        break;
    }

    setSelectedDate(newDate);
    onDateChange?.(newDate);
  }, [selectedDate, viewType, onDateChange]);

  // Get calendar title
  const getCalendarTitle = useMemo(() => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long'
    };

    if (viewType === 'week') {
      const startOfWeek = new Date(selectedDate);
      startOfWeek.setDate(selectedDate.getDate() - selectedDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (viewType === 'day') {
      return selectedDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    return selectedDate.toLocaleDateString('en-US', options);
  }, [selectedDate, viewType]);

  // Get date range for current view
  const getDateRange = useMemo(() => {
    const start = new Date(selectedDate);
    const end = new Date(selectedDate);

    if (viewType === 'month') {
      start.setDate(1);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
    } else if (viewType === 'week') {
      start.setDate(selectedDate.getDate() - selectedDate.getDay());
      end.setDate(start.getDate() + 6);
    } else {
      // Day view - same date
    }

    return { start, end };
  }, [selectedDate, viewType]);

  // Filter tasks for current view
  const filteredTasks = useMemo(() => {
    const { start, end } = getDateRange;

    console.log('[CalendarView Debug] Date range:', { start, end });
    console.log('[CalendarView Debug] Total tasks:', tasks.length);
    console.log('[CalendarView Debug] Tasks with due_date:', tasks.filter(t => t.due_date).length);

    const filtered = tasks.filter(task => {
      if (!task.due_date) {
        console.log('[CalendarView Debug] Task without due_date:', task.title);
        return true; // Include tasks without due dates - they'll go to "Unscheduled" section
      }
      const taskDate = new Date(task.due_date);
      const isInRange = taskDate >= start && taskDate <= end;
      console.log('[CalendarView Debug] Task:', task.title, 'Due:', task.due_date, 'Date:', taskDate, 'InRange:', isInRange);
      return isInRange;
    });

    console.log('[CalendarView Debug] Filtered tasks count:', filtered.length);
    return filtered;
  }, [tasks, getDateRange]);

  // Group tasks by date and collect unscheduled tasks
  const tasksByDate = useMemo(() => {
    const grouped = new Map<string, Task[]>();

    filteredTasks.forEach(task => {
      if (!task.due_date) {
        // Add unscheduled tasks to a special key
        if (!grouped.has('unscheduled')) {
          grouped.set('unscheduled', []);
        }
        grouped.get('unscheduled')!.push(task);
      } else {
        const dateKey = task.due_date.split('T')[0]; // Get YYYY-MM-DD
        if (!grouped.has(dateKey)) {
          grouped.set(dateKey, []);
        }
        grouped.get(dateKey)!.push(task);
      }
    });

    // Sort tasks by daily_rank within each date
    grouped.forEach((tasks, date) => {
      tasks.sort((a, b) => (a.daily_rank || 0) - (b.daily_rank || 0));
    });

    return grouped;
  }, [filteredTasks]);

  // Generate calendar days for month view
  const getCalendarDays = useMemo(() => {
    if (viewType !== 'month') return [];

    const days = [];
    const start = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const firstDayOfWeek = start.getDay();
    const daysInMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfWeek; i++) {
      const prevDate = new Date(start);
      prevDate.setDate(prevDate.getDate() - (firstDayOfWeek - i));
      days.push({
        date: prevDate,
        isCurrentMonth: false,
        isToday: false
      });
    }

    // Add days of the current month
    const today = new Date();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.toDateString() === today.toDateString()
      });
    }

    // Add empty cells to complete the last week
    const totalCells = Math.ceil(days.length / 7) * 7;
    for (let i = days.length; i < totalCells; i++) {
      const nextDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, i - daysInMonth - firstDayOfWeek + 1);
      days.push({
        date: nextDate,
        isCurrentMonth: false,
        isToday: false
      });
    }

    return days;
  }, [selectedDate, viewType]);

  // Drag and drop handlers
  const handleTaskDragStart = (e: React.DragEvent, task: Task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleTaskDragEnd = () => {
    setDraggedTask(null);
    setDragOverDate(null);
  };

  const handleDateDragOver = (e: React.DragEvent, dateStr: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(dateStr);
  };

  const handleDateDragLeave = () => {
    setDragOverDate(null);
  };

  const handleDateDrop = async (e: React.DragEvent, dateStr: string) => {
    e.preventDefault();
    setDragOverDate(null);

    if (!draggedTask || !onTaskUpdate) return;

    // Don't do anything if dropping on the same date
    if (draggedTask.due_date && draggedTask.due_date.split('T')[0] === dateStr) {
      setDraggedTask(null);
      return;
    }

    try {
      // Update task with new date
      await onTaskUpdate(draggedTask.id, {
        due_date: `${dateStr}T${draggedTask.due_date?.split('T')[1] || '23:59:59'}`
      });
    } catch (error) {
      console.error('Error moving task:', error);
    }

    setDraggedTask(null);
  };

  // Context menu handlers
  const handleTaskRightClick = (e: React.MouseEvent, task: Task) => {
    e.preventDefault();
    setContextMenu({
      mouseX: e.clientX - 2,
      mouseY: e.clientY - 4,
      task
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // Quick create task
  const handleQuickCreate = (dateStr: string) => {
    setQuickCreateDate(dateStr);
    setQuickTaskTitle('');
    setQuickCreateOpen(true);
  };

  const handleQuickCreateSubmit = async () => {
    if (!quickTaskTitle.trim() || !onTaskCreate) return;

    try {
      await onTaskCreate({
        title: quickTaskTitle.trim(),
        due_date: `${quickCreateDate}T23:59:59`,
        status: 'pending',
        priority: 'medium'
      });
      setQuickCreateOpen(false);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  // Task component
  const TaskChip = React.memo(({ task, dateStr }: { task: Task; dateStr: string }) => (
    <Chip
      size="small"
      label={
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, maxWidth: 200 }}>
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: PRIORITY_COLORS[task.priority],
              flexShrink: 0
            }}
          />
          <Typography
            variant="caption"
            noWrap
            style={{
              fontWeight: task.status === 'completed' ? 'normal' : 500,
              textDecoration: task.status === 'completed' ? 'line-through' : 'none'
            }}
          >
            {task.daily_rank}. {task.title}
          </Typography>
        </div>
      }
      style={{
        marginBottom: 4,
        height: 24,
        backgroundColor: STATUS_COLORS[task.status],
        color: 'white',
        cursor: 'grab',
      }}
      draggable
      onDragStart={(e) => handleTaskDragStart(e, task)}
      onDragEnd={handleTaskDragEnd}
      onContextMenu={(e) => handleTaskRightClick(e, task)}
      onClick={() => {
        // Handle task click for editing
        console.log('Task clicked:', task);
      }}
    />
  ));

  return (
    <div>
      {/* Calendar Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Typography variant="h5" component="h2">
            {getCalendarTitle}
          </Typography>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigateDate('prev')}>
              <ChevronLeft />
            </IconButton>
            <Button onClick={() => navigateDate('today')} startIcon={<Today />}>
              Today
            </Button>
            <IconButton onClick={() => navigateDate('next')}>
              <ChevronRight />
            </IconButton>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* View Type Selector */}
          <FormControl size="small" style={{ minWidth: 120 }}>
            <Select
              value={viewType}
              onChange={(e) => onViewTypeChange?.(e.target.value as ViewType)}
            >
              <MenuItem value="month">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CalendarMonth fontSize="small" />
                  Month
                </div>
              </MenuItem>
              <MenuItem value="week">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ViewWeek fontSize="small" />
                  Week
                </div>
              </MenuItem>
              <MenuItem value="day">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ViewDay fontSize="small" />
                  Day
                </div>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Show Others Toggle */}
          {onShowAssignedToOthersChange && (
            <Button
              variant={showAssignedToOthers ? "contained" : "outlined"}
              size="small"
              onClick={() => onShowAssignedToOthersChange(!showAssignedToOthers)}
              startIcon={<FilterList />}
            >
              Others' Tasks
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleQuickCreate(selectedDate.toISOString().split('T')[0])}
          >
            Add Task
          </Button>
        </div>
      </div>

      {/* Calendar Body */}
      <Paper style={{ padding: 16 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 64, paddingBottom: 64 }}>
            <CircularProgress />
          </div>
        ) : viewType === 'month' ? (
          <>
            {/* Month View */}
            {/* Day Headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, marginBottom: 8 }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <Typography
                  key={day}
                  variant="subtitle2"
                  textAlign="center"
                  color="text.secondary"
                  style={{ paddingTop: 8, paddingBottom: 8 }}
                >
                  {day}
                </Typography>
              ))}
            </div>

            {/* Calendar Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
              {getCalendarDays.map((day, index) => {
                const dateStr = day.date.toISOString().split('T')[0];
                const dayTasks = tasksByDate.get(dateStr) || [];

                return (
                  <div
                    key={index}
                    style={{
                      minHeight: 120,
                      maxHeight: 200,
                      border: `1px solid ${day.isToday ? '#1976d2' : 'rgba(0,0,0,0.12)'}`,
                      borderRadius: 4,
                      padding: 8,
                      backgroundColor: day.isCurrentMonth
                        ? dragOverDate === dateStr
                          ? 'rgba(0,0,0,0.04)'
                          : '#fff'
                        : 'rgba(0,0,0,0.12)',
                      opacity: day.isCurrentMonth ? 1 : 0.6,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onDragOver={(e) => handleDateDragOver(e, dateStr)}
                    onDragLeave={handleDateDragLeave}
                    onDrop={(e) => handleDateDrop(e, dateStr)}
                    onDoubleClick={() => handleQuickCreate(dateStr)}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={day.isToday ? 'bold' : 'normal'}
                      color={day.isToday ? 'primary.main' : 'text.primary'}
                      style={{ marginBottom: 8, flexShrink: 0 }}
                    >
                      {day.date.getDate()}
                    </Typography>

                    <div
                      style={{
                        flex: 1,
                        overflow: 'auto',
                        minHeight: 0,
                      }}
                    >
                      {dayTasks.map(task => (
                        <TaskChip key={task.id} task={task} dateStr={dateStr} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Unscheduled Tasks Section */}
            {tasksByDate.has('unscheduled') && (
              <div style={{ marginTop: 24 }}>
                <Typography variant="h6" color="text.primary" style={{ marginBottom: 16 }}>
                  Unscheduled Tasks
                </Typography>
                <div
                  style={{
                    border: '1px solid rgba(0,0,0,0.12)',
                    borderRadius: 4,
                    padding: 16,
                    backgroundColor: '#fff',
                    minHeight: 60
                  }}
                  onDragOver={(e) => handleDateDragOver(e, 'unscheduled')}
                  onDragLeave={handleDateDragLeave}
                  onDrop={(e) => {
                    // For unscheduled tasks, we'll just prevent the drop
                    e.preventDefault();
                    setDragOverDate(null);
                    setDraggedTask(null);
                  }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {tasksByDate.get('unscheduled')?.map(task => (
                      <TaskChip key={task.id} task={task} dateStr="unscheduled" />
                    ))}
                  </div>
                  {tasksByDate.get('unscheduled')?.length === 0 && (
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      No unscheduled tasks
                    </Typography>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <Typography variant="body1" color="text.secondary" textAlign="center" style={{ paddingTop: 64, paddingBottom: 64 }}>
            Week and Day views coming soon...
          </Typography>
        )}
      </Paper>

      {/* Quick Create Dialog */}
      <Dialog
        open={quickCreateOpen}
        onClose={() => setQuickCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Task for {new Date(quickCreateDate).toLocaleDateString()}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Task Title"
            value={quickTaskTitle}
            onChange={(e) => setQuickTaskTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && quickTaskTitle.trim()) {
                handleQuickCreateSubmit();
              }
            }}
            style={{ marginTop: 8 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickCreateOpen(false)}>Cancel</Button>
          <Button
            onClick={handleQuickCreateSubmit}
            variant="contained"
            disabled={!quickTaskTitle.trim()}
          >
            Create Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => {
          // Handle edit task
          console.log('Edit task:', contextMenu?.task);
          handleContextMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Task</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => {
          // Handle delete task
          if (contextMenu?.task && onTaskDelete) {
            onTaskDelete(contextMenu.task.id);
          }
          handleContextMenuClose();
        }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Task</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};
